/**
 * Console Capture Wrapper (Frontend)
 *
 * Intercepts console methods to forward logs to the application logger.
 * Preserves original console behavior while capturing logs for observability.
 */

import { logger, formatArgsAsContext, serializeError } from './frontendLogger';

type ConsoleMethods = 'log' | 'info' | 'warn' | 'error' | 'debug';

type OriginalConsoleMethods = {
  [K in ConsoleMethods]: typeof console[K];
};

let isCapturing = false;
let isDispatching = false;
let originalMethods: OriginalConsoleMethods | null = null;

/**
 * Extracts a message string from console arguments.
 */
const extractMessage = (args: unknown[]): string => {
  if (args.length === 0) {
    return '';
  }

  const first = args[0];
  if (typeof first === 'string') {
    return first;
  }

  if (first instanceof Error) {
    return `${first.name}: ${first.message}`;
  }

  try {
    return JSON.stringify(first);
  } catch {
    return String(first);
  }
};

/**
 * Creates an interceptor for a console method.
 */
const createInterceptor = (
  method: ConsoleMethods,
  original: (...args: unknown[]) => void
): ((...args: unknown[]) => void) => {
  return (...args: unknown[]): void => {
    original.apply(console, args);

    if (isDispatching) return;
    isDispatching = true;

    try {
      const message = extractMessage(args);
      const firstIsError = args.length > 0 && args[0] instanceof Error;
      const contextArgs = firstIsError ? args : args.slice(1);
      let context = formatArgsAsContext(contextArgs);

      if (firstIsError && !context) {
        context = serializeError(args[0] as Error);
      }

      switch (method) {
        case 'debug':
          logger.debug(message, context);
          break;
        case 'log':
        case 'info':
          logger.info(message, context);
          break;
        case 'warn':
          logger.warn(message, context);
          break;
        case 'error':
          logger.error(message, context);
          break;
      }
    } finally {
      isDispatching = false;
    }
  };
};

/**
 * Starts capturing console output.
 * Safe to call multiple times (idempotent).
 */
export const startConsoleCapture = (): void => {
  if (isCapturing) {
    return;
  }

  originalMethods = {
    log: console.log.bind(console),
    info: console.info.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console),
    debug: console.debug.bind(console),
  };

  console.log = createInterceptor('log', originalMethods.log);
  console.info = createInterceptor('info', originalMethods.info);
  console.warn = createInterceptor('warn', originalMethods.warn);
  console.error = createInterceptor('error', originalMethods.error);
  console.debug = createInterceptor('debug', originalMethods.debug);

  isCapturing = true;
};

/**
 * Stops capturing console output and restores original methods.
 * Safe to call multiple times (idempotent).
 */
export const stopConsoleCapture = (): void => {
  if (!isCapturing || !originalMethods) {
    return;
  }

  console.log = originalMethods.log;
  console.info = originalMethods.info;
  console.warn = originalMethods.warn;
  console.error = originalMethods.error;
  console.debug = originalMethods.debug;

  originalMethods = null;
  isCapturing = false;
};

/**
 * Returns whether console capture is currently active.
 */
export const isConsoleCaptureActive = (): boolean => {
  return isCapturing;
};
